import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  DigitalCenter,
  DigitalCenterInput,
  ListDigitalCentersResponse,
} from 'shared';

export type { DigitalCenter, DigitalCenterInput };

// ─── Query key factory ────────────────────────────────────────────────────────

export const dcKeys = {
  all: ['digital-centers'] as const,
  list: (params: ListParams) => [...dcKeys.all, 'list', params] as const,
  detail: (id: number) => [...dcKeys.all, 'detail', id] as const,
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ListParams {
  limit?: number;
  offset?: number;
  search?: string;
}

// API returns 422 with a structured body on validation failure.
export interface ApiValidationError {
  message: string;
  errors: Partial<Record<keyof DigitalCenterInput, string>>;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Paginated, searchable list of digital centers. */
export function useDigitalCenters(params: ListParams = {}) {
  const { limit = 10, offset = 0, search = '' } = params;

  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    ...(search ? { search } : {}),
  });

  return useQuery({
    queryKey: dcKeys.list(params),
    queryFn: () =>
      api.get<ListDigitalCentersResponse>(`/api/digital-centers?${qs}`),
    staleTime: 1000 * 30,
    placeholderData: (prev) => prev,
  });
}

/** Single digital center by ID. */
export function useDigitalCenter(id: number | null) {
  return useQuery({
    queryKey: dcKeys.detail(id!),
    queryFn: () => api.get<DigitalCenter>(`/api/digital-centers/${id}`),
    enabled: id !== null,
  });
}

/** Create a new digital center. */
export function useCreateDigitalCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DigitalCenterInput) =>
      api.post<DigitalCenter>('/api/digital-centers', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: dcKeys.all }),
  });
}

/** Update an existing digital center. */
export function useUpdateDigitalCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DigitalCenterInput }) =>
      api.put<DigitalCenter>(`/api/digital-centers/${id}`, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: dcKeys.all });
      qc.invalidateQueries({ queryKey: dcKeys.detail(id) });
    },
  });
}

/** Soft-delete a digital center. */
export function useDeleteDigitalCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/digital-centers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: dcKeys.all }),
  });
}
